import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Creates a Stripe Identity VerificationSession for the current user and
 * returns the hosted verification URL. The user is redirected to Stripe's
 * hosted flow (document + selfie). Stripe posts the final result back to
 * our webhook, which updates `profiles.kyc_status`.
 */
export const createIdentityVerificationSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const secret = process.env.STRIPE_SECRET_KEY;
    if (!secret) throw new Error("Stripe is not configured");

    // Prevent starting a new session if already verified.
    const { data: profile, error: profileErr } = await supabase
      .from("profiles")
      .select("kyc_status, full_name, email")
      .eq("id", userId)
      .maybeSingle();
    if (profileErr) throw new Error(profileErr.message);
    if (profile?.kyc_status === "verified") {
      throw new Error("Your identity is already verified.");
    }

    const Stripe = (await import("stripe")).default;
    const stripe = new Stripe(secret, { apiVersion: "2025-01-27.acacia" as never });

    const origin =
      getRequestHeader("origin") ||
      (getRequestHeader("referer")
        ? new URL(getRequestHeader("referer")!).origin
        : "");
    const returnUrl = `${origin || ""}/verify?stripe_check=1`;

    const session = await stripe.identity.verificationSessions.create({
      type: "document",
      metadata: { user_id: userId },
      options: {
        document: {
          require_matching_selfie: true,
          require_live_capture: true,
          require_id_number: false,
        },
      },
      return_url: returnUrl,
    });

    // Persist session id + move status to pending so we can look up the user
    // in the webhook and reflect a "review in progress" state.
    await supabase.rpc("stripe_finalize_kyc", {
      _user_id: userId,
      _session_id: session.id,
      _status: "pending",
      _reason: undefined,
    });

    return {
      url: session.url,
      client_secret: session.client_secret,
      id: session.id,
    };
  });
